import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { EvieOrb } from '@/components/primitives';
import { manualPageBySlugForLocale, type ManualPage } from '@evi/content';
import { useHelpAudience } from '@/hooks/useHelpAudience';
import { useEvieChatStore } from '@/store/evieChat';
import { useTourStore } from '@/store/tour';
import { HelpTabs } from './HelpTabs';
import { markEvie } from './helpFormat';
import styles from './HelpPage.module.css';

interface Props { onNavigate: (path: string) => void }

/**
 * Category groups for the table of contents, in display order. Each is an
 * ordered list of section slugs under a header. The first header is
 * "Start Here" rather than "Getting Started" so it does not collide with the
 * Getting Started section inside it. The two gated groups render only for the
 * matching audience, headers and all; the driver groups always show.
 */
const TOC_GROUPS: Array<{ header: string; headerKey: string; slugs: string[]; gate?: 'fleet' | 'host' }> = [
  { header: 'Start Here', headerKey: 'groups.startHere', slugs: ['getting-started', 'starting-a-charge', 'payment-and-wallet'] },
  { header: 'Your Charging', headerKey: 'groups.yourCharging', slugs: ['sessions-and-receipts', 'idle-fees', 'favorites', 'rfid-cards', 'store'] },
  { header: 'Rewards', headerKey: 'groups.rewards', slugs: ['points-and-rewards', 'themes', 'coupons'] },
  { header: 'Your Account', headerKey: 'groups.yourAccount', slugs: ['notifications-and-account', 'joining-a-building'] },
  { header: 'Help', headerKey: 'groups.help', slugs: ['guided-tour', 'ask-evie', 'getting-help'] },
  { header: 'For Fleet Drivers', headerKey: 'groups.fleetDrivers', slugs: ['fleets'], gate: 'fleet' },
  { header: 'For Hosts', headerKey: 'groups.hosts', slugs: ['my-home', 'earning-as-a-host', 'getting-paid-stripe-express'], gate: 'host' },
];

/**
 * Help table of contents: the manual's front page. Lists every section and
 * opens a full routed page per section (`/help/<slug>`), the stable routes
 * EVII deep-links to by name. Sections are grouped under category headers
 * (TOC_GROUPS); the For Fleet Drivers and For Hosts groups show only to the
 * matching audience, headers and all (useHelpAudience). A Manual / What's New
 * toggle at the top opens the changelog, and a Chat with EVII button opens
 * the assistant. Reached from the account menu (Profile > Help).
 */
export function HelpPage({ onNavigate }: Props) {
  const { t, i18n } = useTranslation('help');
  const { isFleet, isHost } = useHelpAudience();

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <button
          type="button"
          className={styles.backFab}
          onClick={() => onNavigate('/profile')}
          aria-label={t('back')}
        >
          <ArrowLeft size={18} />
        </button>
        <div className={styles.titleBlock}>
          <h1 className={styles.title}>{t('title')}</h1>
          <p className={styles.subtitle}>{t('subtitle')}</p>
        </div>
      </header>

      <HelpTabs active="manual" onNavigate={onNavigate} />

      {/* Take the tour — relaunch the first-run guided walkthrough on demand.
          Same imperative pattern as the Chat-with-EVII CTA below (open a
          store, not a route), so the tour runs over whatever screen returns
          the driver here. */}
      <button
        type="button"
        className={styles.evieCta}
        onClick={() => useTourStore.getState().open()}
      >
        <span className={styles.evieCtaIcon}>
          <EvieOrb size={22} />
        </span>
        <span className={styles.evieCtaText}>
          <span className={styles.evieCtaTitle}>{t('tour.title')}</span>
          <span className={styles.evieCtaSub}>{t('tour.subtitle')}</span>
        </span>
      </button>

      {/* Start-a-conversation action, distinct from the Ask EVII manual
          section in the Help group (which explains what she does). Same
          launch as the map's quick actions: open the global sheet in
          place — EVII opens over this page and closing returns here. */}
      <button
        type="button"
        className={styles.evieCta}
        onClick={() => useEvieChatStore.getState().openSheet()}
      >
        <span className={styles.evieCtaIcon}>
          <EvieOrb size={22} />
        </span>
        <span className={styles.evieCtaText}>
          <span className={styles.evieCtaTitle}>{markEvie(t('chat.title'))}</span>
          <span className={styles.evieCtaSub}>{t('chat.subtitle')}</span>
        </span>
      </button>

      <nav className={styles.toc} aria-label={t('sectionsAria')}>
        {TOC_GROUPS.map((group) => {
          if (group.gate === 'fleet' && !isFleet) return null;
          if (group.gate === 'host' && !isHost) return null;
          const pages = group.slugs
            // Text localized per key via the @evi/content overlay;
            // slug, audience and related stay single-source (English
            // falls back per key).
            .map((slug) => manualPageBySlugForLocale(slug, i18n.language))
            .filter((p): p is ManualPage => Boolean(p));
          if (pages.length === 0) return null;
          return (
            <Fragment key={group.header}>
              <h2 className={styles.groupLabel}>{t(group.headerKey)}</h2>
              {pages.map((p) => (
                <TocRow key={p.slug} page={p} onNavigate={onNavigate} />
              ))}
            </Fragment>
          );
        })}
      </nav>
    </div>
  );
}

function TocRow({ page, onNavigate }: { page: ManualPage; onNavigate: (path: string) => void }) {
  return (
    <button
      type="button"
      className={styles.tocRow}
      onClick={() => onNavigate(`/help/${page.slug}`)}
    >
      <span className={styles.tocText}>
        <span className={styles.tocTitle}>{markEvie(page.title)}</span>
        <span className={styles.tocSummary}>{page.summary}</span>
      </span>
    </button>
  );
}
